import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTypes,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MuiIcons,
} from '@leap4/frontend';
import { useAppDispatch } from '../../../store/hooks';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { getGetDashboardCatalogueQueryKey } from '../../../api/generated/api';
import { urlService } from '../../routing/urlsService';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import { AXIOS_INSTANCE } from '../../../api/axiosInstance';
import { CalculatedMetricTargetMode } from '../../../api/generated/models/calculatedMetricTargetMode';
import type { CalculatedMetricDto } from '../../../api/generated/models/calculatedMetricDto';
import {
  buildCatalogKeySet,
  buildCustomLabelOptions,
  buildFormulaOperandOptions,
  buildOverrideTargetKeySet,
  buildOverrideTargetOptions,
  clampDecimals,
  normalizeTokensForApi,
} from './calculatedMetrics.constants';
import CalculatedMetricRow from './CalculatedMetricRow';
import ActiveToggleButton from './ActiveToggleButton';
import InternalOnlyToggleButton from './InternalOnlyToggleButton';
import type {
  CalculatedMetricRowState,
  CampaignReportingConfig,
  ReportingConfigEvent,
  ReportingMetricLabel,
} from './campaignForm.types';
import { rowHasDraftContent, validateAllCalculatedMetrics } from './formulaValidator';
import { buildReportTypeIndex, renderReportTypeChips } from './ReportTypeChips';
import { useReportingMetadata } from '../reporting/hooks/useReportingMetadata';

const { Campaign: CampaignIcon } = MuiIcons;

interface CampaignFormData {
  id_campaign: string;
  name: string;
  description: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  payout_override: string;
}

interface Organization {
  id: string;
  name: string;
}

const emptyFormData: CampaignFormData = {
  id_campaign: '',
  name: '',
  description: '',
  is_active: true,
  start_date: '',
  end_date: '',
  payout_override: '',
};

const formatDateForInput = (isoDate: string | null): string => {
  if (!isoDate) return '';
  return isoDate.split('T')[0];
};

const createRowId = (): string =>
  globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `row_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const CampaignForm = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const editMode = !!id;

  const [formData, setFormData] = useState<CampaignFormData>(emptyFormData);
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);

  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [assignedOrgIds, setAssignedOrgIds] = useState<string[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [reportingEvents, setReportingEvents] = useState<ReportingConfigEvent[]>([]);
  const [metricLabels, setMetricLabels] = useState<ReportingMetricLabel[]>([]);
  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetricRowState[]>([]);
  const [loadingReportingConfig, setLoadingReportingConfig] = useState(false);
  const [lockedEventKeys, setLockedEventKeys] = useState<Set<string>>(new Set());
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);

  const metadata = useReportingMetadata();
  const reportTypeIndex = useMemo(() => buildReportTypeIndex(metadata), [metadata]);

  const customLabelOptions = useMemo(
    () => buildCustomLabelOptions(metadata.metrics, metadata.dimensions),
    [metadata.metrics, metadata.dimensions]
  );

  const overrideTargetOptions = useMemo(
    () => buildOverrideTargetOptions(metadata.metrics),
    [metadata.metrics]
  );

  const catalogKeys = useMemo(
    () => buildCatalogKeySet(metadata.metrics, metadata.dimensions),
    [metadata.metrics, metadata.dimensions]
  );

  const overrideTargetKeys = useMemo(
    () => buildOverrideTargetKeySet(overrideTargetOptions),
    [overrideTargetOptions]
  );

  const formulaOperandOptions = useMemo(
    () => buildFormulaOperandOptions(metadata.metrics, reportingEvents),
    [metadata.metrics, reportingEvents]
  );

  const validEventKeys = useMemo(
    () =>
      reportingEvents
        .map((event) =>
          String(event.event || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean),
    [reportingEvents]
  );

  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await AXIOS_INSTANCE.get('/campaigns');
      const campaigns = response.data.data || [];
      const campaign = campaigns.find((c: any) => c.id_campaign === id);
      if (campaign) {
        setFormData({
          id_campaign: campaign.id_campaign,
          name: campaign.name || '',
          description: campaign.description || '',
          is_active: campaign.is_active,
          start_date: formatDateForInput(campaign.start_date),
          end_date: formatDateForInput(campaign.end_date),
          payout_override:
            campaign.payout_override === null || campaign.payout_override === undefined
              ? ''
              : String(campaign.payout_override),
        });
      } else {
        dispatch(setAlert({ msg: `Campaign "${id}" not found`, type: AlertTypes.FAILURE, d: dispatch }));
        history.replace(urlService.campaigns());
      }
    } catch (err) {
      console.error('Failed to fetch campaign:', err);
      dispatch(setAlert({ msg: 'Could not fetch campaign', type: AlertTypes.FAILURE, d: dispatch }));
    } finally {
      setLoading(false);
    }
  }, [id, dispatch, history]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await AXIOS_INSTANCE.get('/campaign-access/organizations');
      setAllOrganizations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  }, []);

  const fetchCampaignAccess = useCallback(async () => {
    if (!id) return;
    setLoadingOrgs(true);
    try {
      const response = await AXIOS_INSTANCE.get(`/campaign-access/by-campaign/${id}`);
      const orgs = response.data.organizations || [];
      const ids = orgs.map((o: { organizationId: string }) => o.organizationId);
      setAssignedOrgIds(ids);
    } catch (err) {
      console.error('Failed to fetch campaign access:', err);
    } finally {
      setLoadingOrgs(false);
    }
  }, [id]);

  const fetchCampaignReportingConfig = useCallback(async () => {
    if (!id) return;
    setLoadingReportingConfig(true);
    try {
      const response = await AXIOS_INSTANCE.get(`/campaigns/${id}/reporting-config`);
      const config = response.data || { events: [], metric_labels: [], calculated_metrics: [] };
      const fetchedEvents = (config.events || []).map(
        (event: ReportingConfigEvent, index: number) => ({
          _rowId: createRowId(),
          event: event.event || '',
          attribution_window_days: Number(event.attribution_window_days) || 1,
          display_label: event.display_label || event.event || '',
          display_order: Number.isFinite(event.display_order as number)
            ? Number(event.display_order)
            : index,
          is_active: event.is_active !== false,
          is_internal_only: event.is_internal_only !== false,
          is_attributed: event.is_attributed !== false,
          description: event.description || '',
        })
      );
      setReportingEvents(fetchedEvents);
      setLockedEventKeys(
        new Set(
          fetchedEvents
            .map((event: ReportingConfigEvent) =>
              String(event.event || '')
                .trim()
                .toLowerCase()
            )
            .filter(Boolean)
        )
      );
      setMetricLabels(
        (config.metric_labels || []).map((metric: ReportingMetricLabel) => ({
          metric_key: metric.metric_key || '',
          display_label: metric.display_label || '',
        }))
      );
      setCalculatedMetrics(
        (config.calculated_metrics || []).map((calc: CalculatedMetricDto, index: number) => ({
          _rowId: createRowId(),
          key: calc.key || '',
          display_label: calc.display_label || '',
          target_mode:
            calc.target_mode === CalculatedMetricTargetMode.override
              ? CalculatedMetricTargetMode.override
              : CalculatedMetricTargetMode.new,
          target_column: calc.target_column || undefined,
          formula: { tokens: normalizeTokensForApi(calc.formula?.tokens || []) },
          format: {
            prefix: calc.format?.prefix || undefined,
            suffix: calc.format?.suffix || undefined,
            decimals: clampDecimals(calc.format?.decimals ?? 2),
          },
          display_order: Number.isFinite(calc.display_order as number)
            ? Number(calc.display_order)
            : index,
          is_active: calc.is_active !== false,
          is_internal_only: (calc as { is_internal_only?: boolean }).is_internal_only !== false,
          _keyEdited: true,
          _collapsed: true,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch campaign reporting config:', err);
      setReportingEvents([]);
      setMetricLabels([]);
      setCalculatedMetrics([]);
      setLockedEventKeys(new Set());
      dispatch(setAlert({
        msg: 'Could not load reporting configuration',
        type: AlertTypes.WARNING,
        d: dispatch,
      }));
    } finally {
      setLoadingReportingConfig(false);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (editMode) {
      fetchCampaign();
      fetchOrganizations();
      fetchCampaignAccess();
      fetchCampaignReportingConfig();
    } else {
      fetchOrganizations();
      setReportingEvents([]);
      setMetricLabels([]);
      setCalculatedMetrics([]);
      setLockedEventKeys(new Set());
    }
  }, [
    editMode,
    fetchCampaign,
    fetchOrganizations,
    fetchCampaignAccess,
    fetchCampaignReportingConfig,
  ]);

  const handleEventChange = (index: number, field: keyof ReportingConfigEvent, value: any) => {
    setReportingEvents((prev) =>
      prev.map((event, i) => (i === index ? { ...event, [field]: value } : event))
    );
  };

  const handleAddEvent = () => {
    setReportingEvents((prev) => [
      ...prev,
      {
        _rowId: createRowId(),
        event: '',
        attribution_window_days: 14,
        display_label: '',
        display_order: prev.length,
        is_active: true,
        is_internal_only: true,
        is_attributed: true,
        description: '',
      },
    ]);
  };

  const handleDeleteEvent = (index: number) => {
    const confirmed = globalThis.confirm(
      "Are you sure you want to remove this event from the system?\n\nThis change will only occur after you press the 'Update Campaign' button at the bottom of the page."
    );
    if (!confirmed) return;
    setReportingEvents((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMetricLabelChange = (
    index: number,
    field: keyof ReportingMetricLabel,
    value: string
  ) => {
    setMetricLabels((prev) =>
      prev.map((metric, i) => (i === index ? { ...metric, [field]: value } : metric))
    );
  };

  const handleAddMetricLabel = () => {
    setMetricLabels((prev) => {
      const used = new Set(prev.map((metric) => metric.metric_key));
      const firstUnused = customLabelOptions.find((option) => !used.has(option.key));
      return [...prev, { metric_key: firstUnused?.key || '', display_label: '' }];
    });
  };

  const handleDeleteMetricLabel = (index: number) => {
    setMetricLabels((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCalculatedMetricChange = (
    index: number,
    patch: Partial<CalculatedMetricRowState>
  ) => {
    setCalculatedMetrics((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const handleAddCalculatedMetric = () => {
    setCalculatedMetrics((prev) => [
      ...prev,
      {
        _rowId: createRowId(),
        key: '',
        display_label: '',
        target_mode: CalculatedMetricTargetMode.new,
        formula: { tokens: [] },
        format: { decimals: 2 },
        display_order: prev.length,
        is_active: true,
        is_internal_only: true,
        _collapsed: false,
      },
    ]);
  };

  const handleDeleteCalculatedMetric = (index: number) => {
    setCalculatedMetrics((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name || undefined,
        description: formData.description || undefined,
        is_active: formData.is_active,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        // Always sent: an empty box means "clear it and go back to deriving", which is a value.
        payout_override:
          formData.payout_override === '' ? null : Number(formData.payout_override),
      };

      if (editMode) {
        await AXIOS_INSTANCE.put(`/campaigns/${formData.id_campaign}`, payload);
        const normalizedEvents = reportingEvents
          .map((event, index) => ({
            event: String(event.event || '')
              .trim()
              .toLowerCase(),
            attribution_window_days: Math.max(1, Number(event.attribution_window_days) || 1),
            display_label: String(event.display_label || '').trim(),
            display_order: Number.isFinite(event.display_order as number)
              ? Number(event.display_order)
              : index,
            is_active: event.is_active !== false,
            is_internal_only: event.is_internal_only !== false,
            is_attributed: event.is_attributed !== false,
            description: String(event.description || '').trim(),
          }))
          .filter((event) => event.event.length > 0);

        const calcRowsToSave = calculatedMetrics.filter(rowHasDraftContent);
        const calcValidation = validateAllCalculatedMetrics(
          calcRowsToSave,
          normalizedEvents.map((event) => event.event),
          overrideTargetKeys,
          catalogKeys
        );
        if (!calcValidation.valid) {
          dispatch(setAlert({
            msg: calcValidation.errors[0] || 'Calculated metric validation failed',
            type: AlertTypes.FAILURE,
            d: dispatch,
          }));
          setSaving(false);
          return;
        }

        const reportingPayload: CampaignReportingConfig = {
          events: normalizedEvents,
          metric_labels: metricLabels
            .map((metric) => ({
              metric_key: String(metric.metric_key || '').trim(),
              display_label: String(metric.display_label || '').trim(),
            }))
            .filter((metric) => metric.metric_key.length > 0 && metric.display_label.length > 0),
          calculated_metrics: calcRowsToSave.map((row, index) => ({
            key: String(row.key || '').trim(),
            display_label: String(row.display_label || '').trim(),
            target_mode: row.target_mode,
            target_column:
              row.target_mode === CalculatedMetricTargetMode.override
                ? row.target_column
                : undefined,
            formula: { tokens: normalizeTokensForApi(row.formula?.tokens || []) },
            format: {
              prefix: row.format?.prefix?.trim() || undefined,
              suffix: row.format?.suffix?.trim() || undefined,
              decimals: clampDecimals(row.format?.decimals ?? 2),
            },
            display_order: Number.isFinite(row.display_order as number)
              ? Number(row.display_order)
              : index,
            is_active: row.is_active !== false,
            is_internal_only: row.is_internal_only !== false,
          })),
        };
        await AXIOS_INSTANCE.put(
          `/campaigns/${formData.id_campaign}/reporting-config`,
          reportingPayload
        );
        queryClient.invalidateQueries(
          getGetDashboardCatalogueQueryKey({ campaign: formData.id_campaign })
        );
        dispatch(setAlert({ msg: 'Campaign updated successfully', type: AlertTypes.SUCCESS, d: dispatch }));
      } else {
        await AXIOS_INSTANCE.post('/campaigns', { id_campaign: formData.id_campaign, ...payload });
        dispatch(setAlert({ msg: 'Campaign created successfully', type: AlertTypes.SUCCESS, d: dispatch }));
      }
      history.push(urlService.campaigns());
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to save campaign';
      dispatch(setAlert({ msg: message, type: AlertTypes.FAILURE, d: dispatch }));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (editMode) {
      setConfirmUpdateDialogOpen(true);
      return;
    }
    handleSave();
  };

  const handleConfirmUpdate = () => {
    setConfirmUpdateDialogOpen(false);
    handleSave();
  };

  const controlledOrgValue = allOrganizations.filter((org) => assignedOrgIds.includes(org.id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          { link: urlService.campaigns(), text: 'Campaigns', icon: <CampaignIcon /> },
          { link: '', text: editMode ? `Edit: ${id}` : 'New Campaign' },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', padding: '30px', borderRadius: '12px' }} elevation={1}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              {editMode ? 'Edit Campaign' : 'Create Campaign'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, textAlign: 'center' }}>
              {editMode
                ? 'Update the campaign details below.'
                : 'Fill in the details to create a new campaign.'}
            </Typography>

            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  autoFocus={!editMode}
                  label="Campaign ID (Slug)"
                  fullWidth
                  variant="outlined"
                  value={
                    editMode
                      ? `${formData.id_campaign} (Campaign ID cannot be changed)`
                      : formData.id_campaign
                  }
                  onChange={(e) =>
                    setFormData({ ...formData, id_campaign: e.target.value.toLowerCase() })
                  }
                  disabled={editMode}
                  helperText={
                    !editMode
                      ? 'URL-safe identifier (lowercase, numbers, hyphens, underscores)'
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  fullWidth
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={4} sx={{ mt: 2 }} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {/* One row so the helper text sits beside the field instead of growing it. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ActiveToggleButton
                    active={formData.is_active}
                    onToggle={(next) => setFormData({ ...formData, is_active: next })}
                  />
                  <TextField
                    label="Payout per conversion"
                    type="number"
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    value={formData.payout_override}
                    onChange={(e) => setFormData({ ...formData, payout_override: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: '0.01', min: 0 }}
                    placeholder="Not set"
                    sx={{ width: 190, flexShrink: 0 }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                    US dollars earned per conversion — not cents, not a percentage. This is the
                    average the customer tells us; it cannot be worked out from our own data, since
                    the real rate is percentage-based and varies by product. Used only where a
                    conversion has not settled yet — the Attributed and Predicted views, both of
                    which are unavailable while this is empty. Real always sums the actual
                    commission and ignores this.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>

            {editMode && (
              <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reporting Configuration
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Edit allowed events, attribution windows, custom labels, and calculated metrics.
                  </Typography>
                  {loadingReportingConfig ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Event Configuration
                      </Typography>
                      {reportingEvents.map((event, index) => (
                        <Grid container spacing={2} key={event._rowId}>
                          <Grid item xs={12} md={2}>
                            <TextField
                              label="Event"
                              fullWidth
                              value={event.event || ''}
                              onChange={(e) => handleEventChange(index, 'event', e.target.value)}
                              disabled={lockedEventKeys.has(
                                String(event.event || '')
                                  .trim()
                                  .toLowerCase()
                              )}
                              inputProps={{ title: 'Event names are case sensitive.' }}
                              helperText={
                                lockedEventKeys.has(
                                  String(event.event || '')
                                    .trim()
                                    .toLowerCase()
                                )
                                  ? 'Event key is locked after first save. Event names are case sensitive.'
                                  : 'Event names are case sensitive.'
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              label="Display Label"
                              fullWidth
                              value={event.display_label || ''}
                              onChange={(e) =>
                                handleEventChange(index, 'display_label', e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              label="Window (days)"
                              type="number"
                              fullWidth
                              disabled={event.is_attributed === false}
                              value={event.attribution_window_days || 1}
                              onChange={(e) =>
                                handleEventChange(
                                  index,
                                  'attribution_window_days',
                                  Number(e.target.value)
                                )
                              }
                              helperText={
                                event.is_attributed === false
                                  ? 'Reporting-only: not attributed'
                                  : undefined
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              label="Description"
                              fullWidth
                              value={event.description || ''}
                              onChange={(e) =>
                                handleEventChange(index, 'description', e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <TextField
                              label="Order"
                              type="number"
                              fullWidth
                              value={event.display_order ?? index}
                              onChange={(e) =>
                                handleEventChange(index, 'display_order', Number(e.target.value))
                              }
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              alignSelf: 'flex-start',

                              mt: { md: 1 },
                            }}
                          >
                            <ActiveToggleButton
                              active={event.is_active !== false}
                              onToggle={(next) => handleEventChange(index, 'is_active', next)}
                            />
                            <InternalOnlyToggleButton
                              internalOnly={event.is_internal_only !== false}
                              onToggle={(next) =>
                                handleEventChange(index, 'is_internal_only', next)
                              }
                            />
                            <Button
                              variant="contained"
                              disableElevation
                              size="small"

                              disabled={lockedEventKeys.has(
                                String(event.event || '')
                                  .trim()
                                  .toLowerCase()
                              )}
                              onClick={() =>
                                handleEventChange(index, 'is_attributed', event.is_attributed === false)
                              }
                              title={
                                lockedEventKeys.has(
                                  String(event.event || '')
                                    .trim()
                                    .toLowerCase()
                                )
                                  ? 'Attribution mode is locked after the event is created — it cannot be changed for an existing event.'
                                  : 'When off, this event is reporting-only: it shows as a metric but is never attributed to impressions (no attribution window).'
                              }

                              style={{
                                minWidth: 116,
                                color: '#ffffff',
                                background: event.is_attributed !== false ? '#5b53d4' : '#607d8b',
                                backgroundImage: 'none',
                                textTransform: 'none',
                              }}
                            >
                              {event.is_attributed !== false ? 'Attributed' : 'Reporting-only'}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleDeleteEvent(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      ))}
                      <Box>
                        <Button variant="outlined" onClick={handleAddEvent}>
                          + Add Event
                        </Button>
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                        Custom Labels
                      </Typography>
                      {metricLabels.map((metric, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={`${metric.metric_key || 'metric'}_${index}`}
                        >
                          <Grid item xs={12} md={5}>
                            <Select
                              variant="outlined"
                              label="Field Key"
                              fullWidth
                              value={metric.metric_key}
                              onChange={(e) =>
                                handleMetricLabelChange(index, 'metric_key', String(e.target.value))
                              }
                              displayEmpty
                            >
                              <MenuItem value="">Choose a field</MenuItem>
                              {customLabelOptions.map((option) => {
                                const usedByOtherRow = metricLabels.some(
                                  (existing, existingIndex) =>
                                    existing.metric_key === option.key && existingIndex !== index
                                );
                                return (
                                  <MenuItem
                                    key={option.key}
                                    value={option.key}
                                    disabled={usedByOtherRow}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                  >
                                    <span>{option.value}</span>
                                    {renderReportTypeChips(reportTypeIndex.get(option.key) ?? [])}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label="Display Label"
                              fullWidth
                              value={metric.display_label}
                              onChange={(e) =>
                                handleMetricLabelChange(index, 'display_label', e.target.value)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteMetricLabel(index)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      ))}
                      <Box>
                        <Button variant="outlined" onClick={handleAddMetricLabel}>
                          + Add Custom Label
                        </Button>
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                        Calculated Metrics
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Define numeric formulas over metrics and events (including other calculated
                        metrics).
                      </Typography>
                      {calculatedMetrics.map((row, index) => (
                        <CalculatedMetricRow
                          key={row._rowId}
                          row={row}
                          index={index}
                          allRows={calculatedMetrics}
                          overrideTargetOptions={overrideTargetOptions}
                          operandOptions={formulaOperandOptions}
                          validEventKeys={validEventKeys}
                          onChange={handleCalculatedMetricChange}
                          onRemove={handleDeleteCalculatedMetric}
                          onNotify={(msg) =>
                            dispatch(setAlert({ msg, type: AlertTypes.SUCCESS, d: dispatch }))
                          }
                        />
                      ))}
                      <Box>
                        <Button variant="outlined" onClick={handleAddCalculatedMetric}>
                          + Add Calculated Metric
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={saving || (!editMode && !formData.id_campaign)}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4 }}
              >
                {saving ? 'Saving...' : editMode ? 'Update Campaign' : 'Create Campaign'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => history.push(urlService.campaigns())}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Grid>

        {editMode && (
          <Grid item xs={12}>
            <Paper
              sx={{ mx: '10px', mb: '10px', mt: 0, padding: '30px', borderRadius: '12px' }}
              elevation={1}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                Associated Organizations
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
                Organizations that have access to this campaign.
              </Typography>

              {loadingOrgs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : controlledOrgValue.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {controlledOrgValue.map((org) => (
                    <Chip key={org.id} label={org.name} color="primary" variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                  No organizations associated yet. Use the Access Manager to set up access.
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmUpdateDialogOpen}
        onClose={() => setConfirmUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Campaign Update</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            Are you sure you want to make changes to this campaign?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Your changes might influence business-facing metrics and data aggregation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmUpdateDialogOpen(false)}
            color="inherit"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Confirm Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignForm;
